import React from 'react';
import PropTypes from 'prop-types';
import css from './CompanyList.module.scss';
import DepartmentInformationPage from 'components/DepartmentInformationPage/DepartmentInformationPage';
import ListSearchForm from 'components/ListSearchForm';
import CompanyListTab from './CompanyListTab';
import Sticky from 'react-sticky-el';

const bodyClass = "company-tabs-sticky";
const SEARCH_QUERY_PARAM_NAME = "q";

class CompanyList extends React.Component {
    static propTypes = {
        businessAreaTabs: PropTypes.arrayOf(PropTypes.shape({
            name: PropTypes.string,
            businessArea: PropTypes.string.isRequired,
        })),
        lang: PropTypes.shape({
            allBusinessAreasTabText: PropTypes.string.isRequired,
            noResultsText: PropTypes.string.isRequired,
            searchInputLabel: PropTypes.string.isRequired,
            searchButtonAriaLabel: PropTypes.string.isRequired,
            searchInputPlaceholder: PropTypes.string.isRequired,
            searchHitsText: PropTypes.string.isRequired,
        }).isRequired,
        companies: PropTypes.arrayOf(PropTypes.shape(DepartmentInformationPage.propTypes))
    }

    constructor(props) {
        super(props);
        this.state = {
            activeBusinessArea: null,
            startX: null,
            searchQuery: "",
            searchQueryMatchedCompanies: [],
            filteredCompanies: [],
            businessAreaCounts: {},
        };
    }

    componentDidMount() {
        const searchParams = new URLSearchParams(window.location.search);
        const searchQuery = searchParams.get(SEARCH_QUERY_PARAM_NAME) || '';
        const initialHash = window.location.hash.slice(1);

        this.setState({
            searchQuery,
            activeBusinessArea: initialHash || null,
        }, this.calculateCurrentListResults);

        this.swipeableContainer.addEventListener('touchstart', this.onTouchStart);
        this.swipeableContainer.addEventListener('touchend', this.onTouchEnd);
    
        window.addEventListener("hashchange", this.onHashChange);
    }

    componentDidUpdate(_, prevState) {
        if (
            prevState.searchQuery !== this.state.searchQuery ||
            prevState.activeBusinessArea !== this.state.activeBusinessArea
        ) {
            this.calculateCurrentListResults();
        }
    }
    
    componentWillUnmount() {
        this.swipeableContainer.removeEventListener('touchstart', this.onTouchStart);
        this.swipeableContainer.removeEventListener('touchend', this.onTouchEnd);

        window.removeEventListener("hashchange", this.onHashChange);
    }
    
    onHashChange = () => {
        const newHash = window.location.hash.slice(1);
        this.setState({
            activeBusinessArea: newHash || null,
        });
    };

    onTouchStart = (e) => {
        this.setState({
            startX: e.touches[0].clientX,
        });
    };

    scrollToCompanyList = () => {
        if (this.swipeableContainer) {
            const offset = this.offsetMarker.offsetHeight - 1;
            const elementPosition = this.swipeableContainer.getBoundingClientRect().top + window.scrollY;

            window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth',
            });

        }
    };
    
    onTabsSticky = isSticky => {   
        if (isSticky){
            document.body.classList.add(bodyClass);
            this.scrollToCompanyList();
        }
        else{
            document.body.classList.remove(bodyClass);
        }
    }
    
    setActiveBusinessArea = (businessArea) => {
        this.setState({
            activeBusinessArea: businessArea,
        });
    
        const queryString = window.location.search || '';
    
        if (businessArea === null) {
            window.history.replaceState({}, document.title, `${window.location.pathname}${queryString}`);
        } else {
            const newUrl = `${window.location.pathname}${queryString}#${businessArea}`;
            if (history.pushState) {
                history.pushState(null, null, newUrl);
            } else {
                location.hash = businessArea;
            }
        }
    
        if (document.body.classList.contains(bodyClass)) {
            this.scrollToCompanyList();
        }
    };

    getActiveBusinessAreaIndex = value => {
        const array = this.props.businessAreaTabs;
        const key = 'businessArea';
        for (let i = 0; i < array.length; i++) {
            if (array[i][key] === value) {
                return i;
            }
        }
        return null;
    }

    scrollToElement = id => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    };

    setActiveBusinessAreaAndScrollToElement = businessArea => {
        this.setActiveBusinessArea(businessArea);
        this.scrollToElement(businessArea);
    }

    setActiveNextBusinessArea = () => {
        if (this.state.activeBusinessArea) {
            const currentIndex = this.getActiveBusinessAreaIndex(this.state.activeBusinessArea);

            if(currentIndex + 1 <  this.props.businessAreaTabs.length){
                this.setActiveBusinessAreaAndScrollToElement(this.props.businessAreaTabs[currentIndex + 1].businessArea);
            }
        }else{
            this.setActiveBusinessAreaAndScrollToElement(this.props.businessAreaTabs[0].businessArea);
        }
    }

    setActivePrevBusinessArea = () => {
        if (this.state.activeBusinessArea) {
            const currentIndex = this.getActiveBusinessAreaIndex(this.state.activeBusinessArea);
            
            if(currentIndex ==  0){
                this.setActiveBusinessArea(null);
                this.scrollToElement('allBusinessAreas');
            }else{
                this.setActiveBusinessAreaAndScrollToElement(this.props.businessAreaTabs[currentIndex - 1].businessArea);
            }
        }
    }

    onTouchEnd = (e) => {
        const { startX } = this.state;
        const endX = e.changedTouches[0].clientX;
        const deltaX = endX - startX;
        const swipeThreshold = 50; 
        
        if (deltaX > swipeThreshold) {
            this.setActivePrevBusinessArea();
        } else if (deltaX < -swipeThreshold) {
            this.setActiveNextBusinessArea();
        }
    };

    onSearchFormSubmit = (searchValue) => {
        const searchParams = new URLSearchParams(window.location.search);
    
        if (searchValue) {
            searchParams.set(SEARCH_QUERY_PARAM_NAME, searchValue);
        } else {
            searchParams.delete(SEARCH_QUERY_PARAM_NAME);
        }
    
        const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}${window.location.hash}`;
        window.history.replaceState({}, '', newUrl);
        this.setActiveBusinessArea(null);    
        this.setState({ searchQuery: searchValue });
    };

    checkIfBillingUnitMatchedSearch = (billingUnit, searchQuery) => {
        return (
            billingUnit.billingName &&
            billingUnit.billingName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }

    calculateCurrentListResults = () => {
        const { searchQuery, activeBusinessArea } = this.state;
        const { companies } = this.props;
    
        let totalMatches = 0;
    
        const filteredCompanies = companies
            .filter((company) => {
                const matchesHeading = searchQuery
                    ? company.heading && company.heading.toLowerCase().includes(searchQuery.toLowerCase())
                    : true;
                
                if (matchesHeading) {
                    totalMatches += 1;
                }
    
                let matchesBillingUnits = false;
                if (searchQuery && company.billingUnits && company.billingUnits.length > 0) {
                    for (let i = 0; i < company.billingUnits.length; i++) {
                        const billingUnit = company.billingUnits[i];
                        if (
                            this.checkIfBillingUnitMatchedSearch(billingUnit, searchQuery)
                        ) {
                            matchesBillingUnits = true;
                            totalMatches += 1;
                        }
                    }
                }
    
                return matchesHeading || matchesBillingUnits;
            })
            .map((company) => {
                let hasBillingUnitMatch = false;
                if (searchQuery && company.billingUnits) {
                    for (let i = 0; i < company.billingUnits.length; i++) {
                        const billingUnit = company.billingUnits[i];
                        if (
                            this.checkIfBillingUnitMatchedSearch(billingUnit, searchQuery)
                        ) {
                            hasBillingUnitMatch = true;
                            break;
                        }
                    }
                }
    
                return {
                    ...company,
                    hasBillingUnitMatch: hasBillingUnitMatch,
                };
            })
            .filter((company) => {
                return (
                    activeBusinessArea === null ||
                    (company.businessAreas &&
                        company.businessAreas.indexOf(activeBusinessArea) !== -1)
                );
            });
    
        this.setState({
            filteredCompanies,
            totalMatches,
        });
    };
     
    render() {
        const { businessAreaTabs, lang } = this.props;
        const { activeBusinessArea, searchQuery, filteredCompanies, totalMatches } = this.state;
    
        return (
            <div className={css.block}>
                <div className={css.search}>
                    <ListSearchForm
                        searchInputLabel={lang.searchInputLabel}
                        searchButtonAriaLabel={lang.searchButtonAriaLabel}
                        searchInputPlaceholder={lang.searchInputPlaceholder}
                        searchParamName={SEARCH_QUERY_PARAM_NAME}
                        defaultValue={searchQuery}
                        totalListItemsCount={totalMatches}
                        listItemsCount={totalMatches}
                        searchHitsText={lang.searchHitsText}
                        noResultsText={lang.noResultsText}
                        noSearchHitsTextDisplayed={!searchQuery}
                        onFormSubmit={this.onSearchFormSubmit}
                        className={css['search-form']}
                    />
                </div>
                <Sticky
                    stickyClassName={css['tabs-sticky']}
                    onFixedToggle={this.onTabsSticky}
                >
                    <div
                        className={css['tabs-scroll-container']}
                        ref={(el) => (this.offsetMarker = el)}
                    >
                        <div className={css['tabs-container']}>
                            <ul className={css.tabs}>
                                {/* All Business Areas Tab */}
                                <CompanyListTab
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setActiveBusinessArea(null);
                                    }}
                                    name={lang.allBusinessAreasTabText}
                                    isActive={!activeBusinessArea}
                                    businessArea="allBusinessAreas"
                                />
                                {/* Business Area Tabs */}
                                {businessAreaTabs &&
                                    businessAreaTabs.map((tab) => (
                                        <CompanyListTab
                                            key={tab.businessArea}
                                            businessArea={tab.businessArea}
                                            name={tab.name}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                this.setActiveBusinessArea(tab.businessArea);
                                            }}
                                            isActive={
                                                activeBusinessArea === tab.businessArea
                                            }
                                        />
                                    ))}
                            </ul>
                        </div>
                    </div>
                </Sticky>
                <div
                    ref={(el) => (this.swipeableContainer = el)}
                    className={css['swipable-container']}
                >
                    {filteredCompanies &&
                        filteredCompanies.map((company) => (
                            <DepartmentInformationPage
                                key={company.contentId}
                                isBillingUnitsListExpanded={company.hasBillingUnitMatch}
                                {...company}
                            />
                        ))}
                    {filteredCompanies && filteredCompanies.length === 0 && (
                        <div className={css['no-results']}>
                            {lang.noResultsText}
                        </div>
                    )}
                </div>
            </div>
        );
    }
    
}

export default CompanyList;



// WEBPACK FOOTER //
// ./src/components/CompanyList/CompanyList.jsx